import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Chip
} from '@mui/material';
import {
  SportsSoccer as SportsSoccerIcon,
  SportsBasketball as SportsBasketballIcon,
  SportsVolleyball as SportsVolleyballIcon,
  DirectionsRun as DirectionsRunIcon,
  SportsMma as SportsMmaIcon,
  SportsTennis as SportsTennisIcon,
  Pool as PoolIcon,
  Sports as SportsIcon,
  DirectionsBoat as DirectionsBoatIcon,
  FitnessCenter as FitnessCenterIcon,
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import SplashScreen from './SplashScreen';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PodiumGif from '../assets/podium.gif';
import { GiBasketballBall, GiSoccerBall } from 'react-icons/gi';
import { TbKarate } from 'react-icons/tb';
import Diversity3Icon from '@mui/icons-material/Diversity3';

// Sport icon mapping
const sportIcons = {
  Football: <GiSoccerBall style={{ color: 'white', fontSize: 20 }} />,
  Basketball: <GiBasketballBall style={{ color: 'white', fontSize: 20 }} />,
  Volleyball: <SportsVolleyballIcon sx={{ color: 'white', fontSize: 20 }} />,
  Athletics: <DirectionsRunIcon sx={{ color: 'white', fontSize: 20 }} />,
  Judo: <TbKarate style={{ color: 'white', fontSize: 20 }} />,
  Badminton: <SportsTennisIcon sx={{ color: 'white', fontSize: 20 }} />,
  Swimming: <PoolIcon sx={{ color: 'white', fontSize: 20 }} />,
  Boxing: <SportsMmaIcon sx={{ color: 'white', fontSize: 20 }} />,
  'Volet Jeunesse': <span style={{ color: 'white', fontSize: 20 }}><Diversity3Icon sx={{ fontSize: 20 }} /></span>,
  Sailing: <DirectionsBoatIcon sx={{ color: 'white', fontSize: 20 }} />,
  Weightlifting: <FitnessCenterIcon sx={{ color: 'white', fontSize: 20 }} />,
};

const UpcomingFixtures = () => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [participantsData, setParticipantsData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFixtures = async () => {
      try {
        setLoading(true);
        
        // Fetch participants
        const participantsSnapshot = await getDocs(collection(db, 'participants'));
        const participantsDataLocal = participantsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          countryId: doc.data().countryId
        }));
        setParticipantsData(participantsDataLocal);

        // Fetch sports to map sportId to sport name and type
        const sportsSnapshot = await getDocs(collection(db, 'sports'));
        const sportsData = sportsSnapshot.docs.reduce((acc, doc) => {
          const data = doc.data();
          acc[doc.id] = { name: data.name || 'Unknown Sport', type: data.type || 'Team', id: doc.id };
          return acc;
        }, {});

        // Fetch fixtures
        const fixturesSnapshot = await getDocs(collection(db, 'fixtures'));
        const fixturesData = fixturesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            sport: sportsData[data.sportId]?.name || 'Unknown Sport',
            sportType: sportsData[data.sportId]?.type || 'Team',
            sportId: data.sportId,
            teams: data.competitors,
            event: data.type === 'Individual' && !data.isHeadToHead ? data.event : null,
            title: data.title || data.event || data.competitors || 'TBC',
            date: data.date,
            time: data.time || '00:00',
            venue: data.venue,
            category: data.category,
            score: data.score || {},
            setScores: data.setScores || [],
            type: data.type,
            isHeadToHead: data.isHeadToHead,
            athletes: data.athletes || [],
            result: data.result || {},
            notes: data.notes || ''
          };
        });

        // Filter fixtures: exclude individual sports with any result, team sports with a winner, and any fixture with time '00:00'
        const filteredFixtures = fixturesData.filter(fixture => {
          // Exclude fixtures with time '00:00'
          if (fixture.time === '00:00') return false;

          if (fixture.type === 'Individual') {
            // Exclude if result object exists (even if empty or winner is 'None')
            return Object.keys(fixture.result).length === 0;
          } else {
            // For team sports, exclude if there is a defined winner
            const hasWinner = fixture.result && fixture.result.winner && fixture.result.winner !== 'None' && fixture.result.winner !== '';
            return !hasWinner;
          }
        });

        // Validate and sort by date and time
        const sortedFixtures = filteredFixtures.sort((a, b) => {
          // Validate date format (expected: YYYY-MM-DD)
          const isValidDate = (dateStr) => {
            const regex = /^\d{4}-\d{2}-\d{2}$/;
            if (!regex.test(dateStr)) return false;
            const date = new Date(dateStr);
            return !isNaN(date.getTime());
          };

          // Validate time format (expected: HH:MM)
          const isValidTime = (timeStr) => {
            const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
            return regex.test(timeStr);
          };

          // Create date objects, using fallback for invalid dates/times
          const dateA = isValidDate(a.date) ? a.date : '9999-12-31';
          const timeA = isValidTime(a.time) ? a.time : '23:59';
          const dateB = isValidDate(b.date) ? b.date : '9999-12-31';
          const timeB = isValidTime(b.time) ? b.time : '23:59';

          const dateTimeA = new Date(`${dateA}T${timeA}+04:00`);
          const dateTimeB = new Date(`${dateB}T${timeB}+04:00`);

          // If dates are invalid, push to end
          if (isNaN(dateTimeA.getTime()) && isNaN(dateTimeB.getTime())) return 0;
          if (isNaN(dateTimeA.getTime())) return 1;
          if (isNaN(dateTimeB.getTime())) return -1;

          return dateTimeA - dateTimeB;
        });

        setFixtures(sortedFixtures);
        setError('');
      } catch (err) {
        console.error('Error fetching fixtures data:', err);
        setError('Failed to load fixtures data');
      } finally {
        setLoading(false);
      }
    };
    fetchFixtures();
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    }
  };

  const translateCategory = (category) => {
    if (category === 'Boys') return 'Garçons';
    if (category === 'Girls') return 'Filles';
    return category;
  };

  const handleFixtureClick = (sportId) => {
    navigate(`/sport/${sportId}`);
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.5); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
          .in-progress-dot {
            width: 10px;
            height: 10px;
            background-color: #FFD700;
            border-radius: 50%;
            animation: pulse 1.5s infinite ease-in-out;
          }
        `}
      </style>
      {loading ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#f5f5f5',
            zIndex: 9999,
          }}
        >
          <SplashScreen />
        </Box>
      ) : (
        <Box sx={{ py: 3, borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h5" color="black" fontWeight="550">
              <PeopleOutlineIcon sx={{ fontSize: 35, color: 'rgba(28,169,201,1)', mt: -1 }} /> Rencontres
            </Typography>
            <Button
              variant="text"
              size='large'
              onClick={() => navigate(`/leaderboard`)}
              sx={{
                color: 'rgb(0,127,255)',
                '&:hover': { bgcolor: 'rgb(42,82,190)', color: 'white' },
                textTransform: 'none',
                fontSize: '0.875rem',
                padding: '6px 12px',
                mt: -1,
              }}
            >
              <img src={PodiumGif} width={50} />
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body" color="black" fontWeight="400">
              Voici les prochaines rencontres à venir. Cliquez sur l'une d'elles pour plus d'informations.
            </Typography>
          </Box>
          
          {error && (
            <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>
              {error}
            </Typography>
          )}
          {fixtures.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#042940', mb: 2 }}>
              Aucune rencontre à venir disponible.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mt: 4 }}>
              {fixtures.slice(0, 20).map(fixture => {
                // Determine if score should be displayed
                let scoreDisplay = '';
                let hasValidScore = false;
                
                if (fixture.type === 'Team') {
                  if (fixture.score.team1Score != null && fixture.score.team2Score != null && 
                      fixture.score.team1Score !== '' && fixture.score.team2Score !== '') {
                    scoreDisplay = `${fixture.score.team1Score}-${fixture.score.team2Score}`;
                    hasValidScore = true;
                  }
                } else {
                  const individualScores = Object.entries(fixture.score)
                    .map(([id, score]) => score && score !== '' && score !== 'None' ? `${participantsData.find(p => p.id === id)?.name || id}: ${score}` : null)
                    .filter(s => s);
                  if (individualScores.length > 0) {
                    scoreDisplay = individualScores.join(', ');
                    hasValidScore = true;
                  }
                }
                
                // Add Volleyball set scores if applicable
                if (fixture.sport === 'Volleyball' && fixture.setScores.length > 0) {
                  scoreDisplay += `, Sets: ${fixture.setScores.join(', ')}`;
                  hasValidScore = true;
                }

                // Check if fixture has no winner but has valid scores for in-progress animation
                const showInProgress = hasValidScore && (!fixture.result || !fixture.result.winner || fixture.result.winner === '' || fixture.result.winner === 'None');

                return (
                  <Card
                    key={`${fixture.sport}-${fixture.id}`}
                    onClick={() => handleFixtureClick(fixture.sportId)}
                    sx={{
                      position: 'relative',
                      boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
                      backgroundColor: 'rgb(0,127,255)',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundImage: 'linear-gradient(159deg, rgba(0,127,255,0.8) 0%, rgba(42,82,190,0.8) 100%)',
                      },
                    }}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }} color="white">
                          <b>{fixture.sport},</b> {translateCategory(fixture.category)}
                        </Typography>
                        <Typography variant="h6" color="white">
                          {fixture.type === 'Individual' ? fixture.title : (fixture.teams || fixture.event)}
                        </Typography>
                        {hasValidScore && (
                          <Box sx={{ mx: 'auto', textAlign: 'center' }} alignItems="center" gap={1} mb={1}>
                            <Typography variant="h6" sx={{ fontWeight: 800, textAlign: 'center', background: 'white', borderRadius: '10px', color: 'darkblue', mx: 8 }}>
                              {scoreDisplay}
                            </Typography>
                          </Box>
                        )}
                        {fixture.notes && fixture.notes !== '' && (
                          <Typography variant="body2" color="white" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {fixture.notes}
                          </Typography>
                        )}
                        <Typography variant="caption" color="white">
                          📍 {fixture.venue}
                        </Typography>
                        <Typography variant="subtitle2" color="white">
                          {fixture.date} @ {fixture.time}
                        </Typography>
                      </Box>
                    </CardContent>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        color: 'white',
                      }}
                    >
                      {sportIcons[fixture.sport] || <SportsIcon />}
                    </Box>
                    {showInProgress && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                        }}
                      >
                        <div className="in-progress-dot" />
                      </Box>
                    )}
                  </Card>
                );
              })}
            </Stack>
          )}
          <br />
          <br />
          <br />
        </Box>
      )}
    </>
  );
};

export default UpcomingFixtures;